import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { IconCheck } from '@tabler/icons-react-native';
import { getImageVariantForSize, getImageVariantUrl } from 'app/lib/image-variants';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useSharingPlaceMembers } from 'app/network/sharing-place';
import type { SharingPlace } from 'app/types/sharing-place';
import { useTranslation } from 'react-i18next';
import { PixelRatio, Pressable, StyleSheet } from 'react-native';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { useNavigation } from 'app/hooks/navigation';
import { useOneUserDistance, useUser } from 'app/network/user';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { GeoPin } from 'app/design/icons/geo-pin';
import { getTodayOpeningStatus } from 'app/lib/opening-hours';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

const CARD_WIDTH = 320;
const COVER_HEIGHT = 130;
const AVATAR_SIZE = 28;
const PLACE_LOGO_SIZE = 40;
const AVATAR_STACK_OFFSET = Math.round(AVATAR_SIZE * 0.6);

const styles = StyleSheet.create({
  cardShadow: {
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
  },
});

function SharingPlaceCard({ place }: { place: SharingPlace }) {
  const { t } = useTranslation('neighbour-profile');
  const membersQuery = useSharingPlaceMembers(place.placeUserId);
  const members = membersQuery.data ?? [];
  const { data: user } = useUser();
  const isMember = members.some((m) => m.userId === user?.id);
  const navigation = useNavigation();
  const placeDetails = place.placeUser?.profile?.placeDetails;
  const profile = place.placeUser?.profile;

  const coverPicture = placeDetails?.coverPictures?.image1;
  const coverUrl = (() => {
    return typeof coverPicture === 'string' && coverPicture
      ? getImageVariantUrl(
          coverPicture,
          getImageVariantForSize(
            PixelRatio.getPixelSizeForLayoutSize(CARD_WIDTH),
            PixelRatio.getPixelSizeForLayoutSize(COVER_HEIGHT),
          ),
        )
      : undefined;
  })();
  const logoUrl = getImageVariantUrl(
    profile?.picture,
    getImageVariantForSize(
      PixelRatio.getPixelSizeForLayoutSize(PLACE_LOGO_SIZE),
      PixelRatio.getPixelSizeForLayoutSize(PLACE_LOGO_SIZE),
    ),
  );

  const placeName = [profile?.firstName ?? '', profile?.lastName ?? ''].filter(Boolean).join(' ');

  const { isOpen, closingTime } = getTodayOpeningStatus(placeDetails?.openingHours);

  const memberAvatars = members.filter((m) => !!m.user?.profile?.picture).slice(0, 3);
  const memberCount = members.length;

  const { formatDistanceWithoutText } = useDistanceUnit();

  const distance = formatDistanceWithoutText(useOneUserDistance(place.placeUserId).data?.distance);

  return (
    <Column
      backgroundColor="white"
      borderColor="surface.disabled"
      borderRadius="radius.lg"
      borderWidth={1}
      marginVertical="spacing.general.x2"
      overflow="hidden"
      style={styles.cardShadow}
      width={CARD_WIDTH}
    >
      <Pressable onPress={() => navigateToUserProfile(navigation, place.placeUserId)}>
        {/* Cover photo */}
        <Column height={COVER_HEIGHT}>
          {coverUrl ? (
            <PCImage
              cachePolicy="memory-disk"
              contentFit="cover"
              height={COVER_HEIGHT}
              recyclingKey={coverUrl}
              source={{ uri: coverUrl }}
              width={CARD_WIDTH}
            />
          ) : (
            <Column backgroundColor="surface.tertiary-neutral" height={COVER_HEIGHT} width={CARD_WIDTH} />
          )}

          {/* "Je fréquente ce lieu" badge */}
          {isMember ? (
            <Row
              alignItems="center"
              backgroundColor="surface.default"
              borderRadius="radius.xl"
              gap="spacing.general.x1"
              left={theme.spacing['spacing.general.x2']}
              paddingHorizontal="spacing.general.x2"
              paddingVertical="spacing.general.x0,5"
              position="absolute"
              top={theme.spacing['spacing.general.x2']}
            >
              <IconCheck color={theme.colors['icon.success']} size={16} strokeWidth={ICON_STROKE_WIDTH} />
              <Text color="text.positive" fontSize={11} fontWeight="600" variant="bodyBase">
                {t('place.frequenting')}
              </Text>
            </Row>
          ) : null}
        </Column>

        {/* Info section */}
        <Column gap="spacing.general.x2" padding="spacing.general.x4" paddingTop="spacing.general.x1">
          {/* Logo + name row */}
          <Row alignItems="center" gap="spacing.general.x2">
            {logoUrl ? (
              <PCImage
                aspectRatio={1}
                cachePolicy="memory-disk"
                contentFit="cover"
                flexShrink={0}
                height={PLACE_LOGO_SIZE}
                recyclingKey={logoUrl}
                source={{ uri: logoUrl }}
                style={{ borderRadius: PLACE_LOGO_SIZE / 2 }}
              />
            ) : (
              <Column
                backgroundColor="surface.disabled"
                flexShrink={0}
                height={PLACE_LOGO_SIZE}
                style={{ borderRadius: PLACE_LOGO_SIZE / 2 }}
                width={PLACE_LOGO_SIZE}
              />
            )}
            <Text flex={1} fontSize={14} fontWeight="700" numberOfLines={2} variant="bodyBase">
              {placeName}
            </Text>
          </Row>

          {/* Opening hours + distance */}
          <Row alignItems="center" gap="spacing.general.x1">
            {isOpen ? (
              <Row alignItems="center" gap="spacing.general.x1">
                <Text color="text.positive" fontSize={12} fontWeight="600">
                  {t('place.open')}
                </Text>
                {closingTime ? (
                  <Text fontSize={12} variant="bodyBase">
                    {t('place.until', { time: closingTime })}
                  </Text>
                ) : null}
              </Row>
            ) : (
              <Text color="icon.error" fontSize={12} fontWeight="600">
                {t('place.closed')}
              </Text>
            )}
            {distance ? (
              <Row alignItems="center" gap="spacing.general.x0,5">
                <Text fontSize={12} variant="bodyBase">
                  ·
                </Text>
                <GeoPin color={theme.colors['text.secondary']} height={14} width={14} />
                <Text fontSize={12} numberOfLines={1} variant="bodyBase">
                  {distance}
                </Text>
              </Row>
            ) : null}
          </Row>

          {/* Members */}
          {memberCount > 0 ? (
            <Row alignItems="center" gap="spacing.general.x2">
              {/* Overlapping avatars */}
              {memberAvatars.length > 0 ? (
                <Row
                  flexShrink={0}
                  height={AVATAR_SIZE}
                  width={AVATAR_SIZE + (memberAvatars.length - 1) * AVATAR_STACK_OFFSET}
                >
                  {memberAvatars.map((member, i) => (
                    <PCImage
                      key={member.id}
                      aspectRatio={1}
                      borderColor="white"
                      borderWidth={2}
                      cachePolicy="memory-disk"
                      height={AVATAR_SIZE}
                      recyclingKey={`${member.id}-sharing-place-avatar`}
                      source={{
                        uri: getImageVariantUrl(member.user!.profile!.picture, 'mini'),
                      }}
                      style={{
                        borderRadius: AVATAR_SIZE / 2,
                        left: i * AVATAR_STACK_OFFSET,
                        position: 'absolute',
                        zIndex: memberAvatars.length - i,
                      }}
                    />
                  ))}
                </Row>
              ) : null}
              <Text fontSize={11} numberOfLines={2} variant="bodyBase" flex={1}>
                {t(`place.members${memberCount === 1 ? '_one' : ''}`, { count: memberCount })}
              </Text>
            </Row>
          ) : null}
        </Column>
      </Pressable>
    </Column>
  );
}

export { SharingPlaceCard };
